import { FC } from "react";

import { SATOSContentRender } from "@/app/application/SATOSContentRender";
import { useTime } from "@/app/application/TimeContext";
import { Translate } from "@/app/application/Translate";
import { useTrainingBack } from "@/features/training/hooks/useTrainingBack";
import { formatDateTime } from "@/features/training/lib/trainingDateUtils";
import { TrainingDrillSessionStatus } from "@/features/training/model/Training/TrainingDrillSession";
import { SessionKpiParameters } from "@/features/training/pages/drill-tab/panel-right/drill-session/SessionKpiParameters";
import { TrainingDrillSessionActions } from "@/features/training/pages/drill-tab/panel-right/drill-session/TrainingDrillSessionActions";
import { DrillSessionSkeleton } from "@/features/training/pages/training-tab/CoursesSkeletons";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import {
	Box,
	Chip,
	Divider,
	IconButton,
	Paper,
	Stack,
	Tooltip,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";

import { useFetchDrill, useFetchDrillSession } from "@/service/TrainingQueries";

const yesNo = (v?: boolean) =>
	v ? (
		<Translate String="Common.Yes">Yes</Translate>
	) : (
		<Translate String="Common.No">No</Translate>
	);

const statusColor = (status?: TrainingDrillSessionStatus) => {
	switch (status) {
		case TrainingDrillSessionStatus.Active:
			return "info";
		case TrainingDrillSessionStatus.Completed:
			return "success";
		case TrainingDrillSessionStatus.Archived:
			return "default";
		default:
			return "text.secondary";
	}
};

interface DrillSessionProps {
	drillId: number;
	sessionId: number;
}

export const DrillSession: FC<DrillSessionProps> = ({ drillId, sessionId }) => {
	const theme = useTheme();
	const handleBack = useTrainingBack();
	const { timezone } = useTime();

	const { isLoading: isLoadingDrill, data: drill } = useFetchDrill(drillId);
	const { isLoading: isLoadingSession, data: session } =
		useFetchDrillSession(sessionId);

	if (isLoadingDrill || isLoadingSession || !drill || !session) {
		return (
			<Box
				sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
			>
				<DrillSessionSkeleton />
			</Box>
		);
	}

	const sessionDate = formatDateTime(session.Date, timezone);
	const finishDate =
		session.FinishDate && session.Status !== TrainingDrillSessionStatus.Active
			? formatDateTime(session.FinishDate, timezone)
			: null;
	const statusPalette = statusColor(session.Status);

	return (
		<Box
			sx={{
				flex: 1,
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			{/* Compact header – aligned with TrainingDrill */}
			<Box
				sx={{
					flexShrink: 0,
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 1.5,
					flexWrap: "wrap",
					py: 1.15,
					px: { xs: 2, sm: 2.5 },
					borderBottom: 1,
					borderColor: "divider",
				}}
			>
				<Tooltip
					title={<Translate String="Common.Back">Back</Translate>}
					enterDelay={400}
				>
					<IconButton
						onClick={handleBack}
						size="small"
						sx={{
							color: "primary.main",
							"&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) },
						}}
					>
						<ArrowBackIosIcon sx={{ fontSize: 18 }} />
					</IconButton>
				</Tooltip>

				<Box sx={{ flex: 1, minWidth: 280 }}>
					<Typography
						variant="caption"
						sx={{
							display: "block",
							mb: 0.05,
							color: "primary.main",
							fontWeight: 700,
							letterSpacing: "0.08em",
							textTransform: "uppercase",
						}}
					>
						<Translate String="Auto.Features.Training.Pages.DrillTab.PanelRight.DrillSession.DrillSession.L131C7.DrillSession">Drill session</Translate>
					</Typography>
					<Typography variant="subtitle1" fontWeight={700} noWrap title={sessionDate}>
						<Translate String="Auto.Features.Training.Pages.DrillTab.PanelRight.DrillSession.DrillSession.L134C82.SessionDetails">Session details</Translate>
					</Typography>
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{ mt: 0.15 }}
						noWrap
						title={drill.Name}
					>
						{sessionDate}
					</Typography>
					<Stack
						direction="row"
						spacing={0.75}
						useFlexGap
						flexWrap="wrap"
						sx={{
							mt: 0.5,
						}}
					>
						<Chip
							size="small"
							label={session.Status || "—"}
							sx={{
								height: 24,
								borderRadius: 2,
								fontWeight: 700,
								bgcolor:
									statusPalette === "info" || statusPalette === "success"
										? alpha(theme.palette[statusPalette].main, 0.1)
										: alpha(theme.palette.text.primary, 0.08),
								color:
									statusPalette === "info" || statusPalette === "success"
										? `${statusPalette}.main`
										: "text.secondary",
							}}
						/>
						<Chip
							size="small"
							label={drill.Name}
							sx={{
								height: 24,
								maxWidth: "100%",
								borderRadius: 2,
								bgcolor: alpha(theme.palette.primary.main, 0.08),
								fontWeight: 700,
								"& .MuiChip-label": {
									display: "block",
									overflow: "hidden",
									textOverflow: "ellipsis",
								},
							}}
						/>
						<Chip
							size="small"
							label={`Stand in ${session.Standin ? "Yes" : "No"}`}
							sx={{
								height: 24,
								borderRadius: 2,
								bgcolor: alpha(theme.palette.info.main, 0.08),
								fontWeight: 700,
							}}
						/>
						{finishDate && (
							<Chip
								size="small"
								label={`Finished ${finishDate}`}
								sx={{
									height: 24,
									borderRadius: 2,
									bgcolor: alpha(theme.palette.success.main, 0.1),
									fontWeight: 700,
								}}
							/>
						)}
					</Stack>
				</Box>

				<TrainingDrillSessionActions
					sessionId={session.Id}
					status={session.Status}
					kpiParameters={session.KpiParameters}
				/>
			</Box>

			{/* Overview card – full width */}
			<Box
				sx={{
					flexShrink: 0,
					px: { xs: 2, sm: 2.5 },
					py: 1.35,
					borderBottom: 1,
					borderColor: "divider",
				}}
			>
				<Paper
					variant="outlined"
					sx={{
						p: 2,
						borderRadius: 3,
						borderColor: "divider",
					}}
				>
					<Typography
						variant="caption"
						sx={{
							display: "block",
							mb: 0.1,
							color: "primary.main",
							fontWeight: 700,
							letterSpacing: "0.08em",
							textTransform: "uppercase",
						}}
					>
						<Translate String="Training.Drill.Overview">Overview</Translate>
					</Typography>
					<Box
						sx={{
							mt: 1.5,
							display: "grid",
							gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
							gap: 2,
						}}
					>
						<Box>
							<Typography
								variant="caption"
								color="text.secondary"
								display="block"
							>
								<Translate String="Training.Session.Date">Date</Translate>
							</Typography>
							<Typography variant="body2" fontWeight={500}>
								{formatDateTime(session.Date, timezone)}
							</Typography>
						</Box>
						<Box>
							<Typography
								variant="caption"
								color="text.secondary"
								display="block"
							>
								<Translate String="Training.Session.FinishDate">
									Finish date
								</Translate>
							</Typography>
							<Typography variant="body2" fontWeight={500}>
								{formatDateTime(session.FinishDate, timezone)}
							</Typography>
						</Box>
						<Box>
							<Typography
								variant="caption"
								color="text.secondary"
								display="block"
							>
								<Translate String="Training.Session.Responsible">
									Responsible
								</Translate>
							</Typography>
							<Typography variant="body2" fontWeight={500}>
								{session.Responsible?.Name || "—"}
							</Typography>
						</Box>
						<Box>
							<Typography
								variant="caption"
								color="text.secondary"
								display="block"
							>
								<Translate String="Training.Session.StandIn">
									Stand in
								</Translate>
							</Typography>
							<Typography variant="body2" fontWeight={500}>
								{yesNo(session.Standin)}
							</Typography>
						</Box>
					</Box>
				</Paper>
			</Box>

			{/* Scrollable content */}
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					overflowY: "auto",
					overflowX: "hidden",
					px: { xs: 2, sm: 2.5 },
					py: 1.5,
					display: "flex",
					flexDirection: "column",
					gap: 2,
				}}
			>
				{/* Information */}
				{(session.Information || session.Comment) && (
					<Paper
						variant="outlined"
						sx={{
							p: 2,
							mb: 2,
							borderRadius: 2,
							borderColor: "divider",
						}}
					>
						{session.Information && (
							<Box>
								<Typography
									variant="overline"
									color="text.secondary"
									sx={{ letterSpacing: 0.8 }}
								>
									<Translate String="Training.Session.Information">
										Information
									</Translate>
								</Typography>
								<Box sx={{ mt: 1 }}>
									<SATOSContentRender
										isNotProcedureRender={true}
										content={session.Information}
									/>
								</Box>
							</Box>
						)}
						{session.Information && session.Comment && (
							<Divider sx={{ my: 2 }} />
						)}
						{session.Comment && (
							<Box>
								<Typography
									variant="overline"
									color="text.secondary"
									sx={{ letterSpacing: 0.8 }}
								>
									<Translate String="Training.Session.Comment">
										Comment
									</Translate>
								</Typography>
								<Typography
									variant="body2"
									sx={{ mt: 1, whiteSpace: "pre-wrap" }}
								>
									{session.Comment}
								</Typography>
							</Box>
						)}
					</Paper>
				)}

				{/* KPI Parameters */}
				{session?.KpiParameters?.length > 0 && (
					<SessionKpiParameters kpis={session.KpiParameters} compact />
				)}
			</Box>
		</Box>
	);
};
